import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Crosshair, FileText, Loader2, Quote, UserSearch, X } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  getWitness,
  listContradictions,
  listWitnesses,
  runWitnessContradiction,
  type Contradiction,
  type Witness,
  type WitnessContradictionResult,
  type WitnessDetail,
} from '@/lib/api-client';
import { useSession } from '@/contexts/SessionContext';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';

// Phase 10.6 step 5 — live witness support in the hearing right column,
// beneath the anchor banner. When a witness's name is detected in the
// transcript (or the operator selects one), their cross-exam plan admission
// and accepted contradictions surface here, plus the "Witness contradiction"
// quick action: last transcript segment vs their prior statements, returning
// the prior quote with source and the put-to-the-witness question.

const DETECTION_WINDOW_CHARS = 2500;
const SEGMENT_CHARS = 1500;

/** Case-insensitive detection of a witness in recent transcript: full name,
 *  or the surname when it is >= 4 chars and unique among the matter's
 *  witnesses. Exported for tests. */
export function detectWitness(recentTranscript: string, witnesses: Witness[]): Witness | null {
  if (witnesses.length === 0 || recentTranscript.length === 0) return null;
  const hay = recentTranscript.toLowerCase();
  // Full-name match wins outright; later mention wins between witnesses.
  let best: { witness: Witness; index: number } | null = null;
  for (const w of witnesses) {
    const idx = hay.lastIndexOf(w.name.toLowerCase());
    if (idx >= 0 && (!best || idx > best.index)) best = { witness: w, index: idx };
  }
  if (best) return best.witness;
  // Surname fallback.
  const surnameOf = (w: Witness): string => w.name.trim().split(/\s+/).slice(-1)[0] ?? '';
  const counts = new Map<string, number>();
  for (const w of witnesses) {
    const s = surnameOf(w).toLowerCase();
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  for (const w of witnesses) {
    const surname = surnameOf(w);
    if (surname.length < 4 || (counts.get(surname.toLowerCase()) ?? 0) > 1) continue;
    const re = new RegExp(`\\b${surname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    const m = re.exec(hay);
    if (m && (best === null || m.index > best.index)) {
      best = { witness: w, index: m.index };
    }
  }
  return best?.witness ?? null;
}

export function WitnessPanel({ matterId }: { matterId: string | null }) {
  const { sessionId } = useSession();
  const { transcript } = useTranscriptionContext();
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [manuallySelected, setManuallySelected] = useState(false);
  const [detail, setDetail] = useState<WitnessDetail | null>(null);
  const [accepted, setAccepted] = useState<Contradiction[]>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<WitnessContradictionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matterId) {
      setWitnesses([]);
      return;
    }
    let active = true;
    listWitnesses(matterId)
      .then((w) => {
        if (active) setWitnesses(w);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [matterId]);

  // Detection runs over the recent transcript tail. A manual selection is
  // never overridden; auto-detection only fills an empty selection or updates
  // a previous auto-detection.
  const recentTail = transcript.slice(-DETECTION_WINDOW_CHARS);
  const detected = useMemo(() => detectWitness(recentTail, witnesses), [recentTail, witnesses]);
  const detectedRef = useRef<string | null>(null);
  useEffect(() => {
    detectedRef.current = detected?.id ?? null;
    if (detected && !manuallySelected && detected.id !== selectedId) {
      setSelectedId(detected.id);
      setResult(null);
    }
  }, [detected, manuallySelected, selectedId]);

  const selected = witnesses.find((w) => w.id === selectedId) ?? null;

  const loadSelected = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      setAccepted([]);
      return;
    }
    const [d, cs] = await Promise.all([
      getWitness(selectedId).catch(() => null),
      listContradictions(selectedId).catch(() => [] as Contradiction[]),
    ]);
    setDetail(d);
    setAccepted(cs.filter((c) => c.status === 'accepted'));
  }, [selectedId]);

  useEffect(() => {
    void loadSelected();
  }, [loadSelected]);

  async function fireContradictionCheck(): Promise<void> {
    if (!sessionId || !selectedId) return;
    const segment = transcript.slice(-SEGMENT_CHARS).trim();
    if (!segment) {
      setError('No transcript yet — there is nothing said today to compare.');
      return;
    }
    setError(null);
    setRunning(true);
    setResult(null);
    try {
      const r = await runWitnessContradiction(sessionId, selectedId, segment);
      setResult(r.result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Contradiction check failed.');
    } finally {
      setRunning(false);
    }
  }

  if (!matterId || witnesses.length === 0) {
    return null; // no witnesses on the matter: keep the column clean
  }

  const activePlan =
    detail?.crossExamPlans.find((p) => p.status === 'live') ??
    detail?.crossExamPlans.find((p) => p.status === 'rehearsed') ??
    detail?.crossExamPlans.find((p) => p.status === 'draft') ??
    null;

  return (
    <Card variant="default" padding="sm" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <UserSearch className="h-3.5 w-3.5" /> Witness
        </p>
        {detected && detected.id === selectedId && !manuallySelected ? (
          <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">
            detected in transcript
          </span>
        ) : null}
      </div>

      <select
        value={selectedId}
        onChange={(e) => {
          setSelectedId(e.target.value);
          setManuallySelected(e.target.value !== '');
          setResult(null);
          setError(null);
        }}
        className="w-full rounded-md border border-stone-800 bg-stone-900 px-2 py-1.5 text-sm text-stone-200 focus:border-amber-700/60 focus:outline-none"
        aria-label="Select witness"
      >
        <option value="">— no witness selected —</option>
        {witnesses.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>

      {selected ? (
        <div className="space-y-2">
          {activePlan ? (
            <Link
              to={`/plans/${activePlan.id}`}
              className="block rounded-md border border-amber-900/40 bg-amber-950/20 p-2 hover:border-amber-700/60"
            >
              <p className="text-[10px] uppercase tracking-[0.15em] text-amber-400">
                Plan ({activePlan.status}) — open
              </p>
              <p className="mt-0.5 line-clamp-2 text-xs text-stone-300">
                {activePlan.title || 'Cross-examination plan'}
              </p>
            </Link>
          ) : (
            <p className="text-xs text-stone-600">
              No cross-exam plan yet —{' '}
              <Link to={`/matters/${matterId}/witnesses`} className="text-amber-500 hover:underline">
                build one
              </Link>
              .
            </p>
          )}

          {accepted.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                Accepted contradictions ({accepted.length})
              </p>
              {accepted.slice(0, 3).map((c) => (
                <div key={c.id} className="rounded border border-stone-800/60 bg-stone-900/40 p-2">
                  <p className="line-clamp-3 text-xs text-stone-300">“{c.quotedText}”</p>
                  <p className="mt-1 flex items-center gap-1 text-[10px] text-stone-500">
                    <FileText className="h-3 w-3" /> {c.filename}
                    {!c.verified ? <span className="text-red-400">· UNVERIFIED</span> : null}
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            disabled={running || !sessionId}
            onClick={() => void fireContradictionCheck()}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-amber-700/50 bg-amber-950/30 px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-amber-300 hover:bg-amber-900/30 disabled:opacity-40"
          >
            {running ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Crosshair className="h-3.5 w-3.5" />
            )}
            {running ? 'Checking prior statements…' : 'Witness contradiction'}
          </button>

          {error ? <p className="text-xs text-red-400">{error}</p> : null}

          {result ? (
            <div
              className={`relative rounded-md border p-2.5 ${
                result.conflict
                  ? 'border-amber-700/60 bg-amber-950/20'
                  : 'border-stone-800 bg-stone-900/40'
              }`}
            >
              <button
                type="button"
                onClick={() => setResult(null)}
                className="absolute right-2 top-2 text-stone-600 hover:text-stone-400"
                aria-label="Dismiss result"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              {result.conflict ? (
                <div className="space-y-1.5 pr-5">
                  <p className="text-sm font-medium text-stone-100">{result.suggestedQuestion}</p>
                  <p className="flex gap-1.5 text-xs text-stone-400">
                    <Quote className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                    <span>
                      Prior: “{result.priorQuote}” — {result.source}
                    </span>
                  </p>
                  {result.verified ? (
                    <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">
                      both quotes verified verbatim
                    </p>
                  ) : (
                    <p className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-red-300">
                      <AlertTriangle className="h-3 w-3" /> unverified — check the source before
                      putting this
                    </p>
                  )}
                </div>
              ) : (
                <p className="pr-5 text-xs text-stone-400">{result.reason}</p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
